/**
 * PitchPrint Save for Later Redirect Handler
 * Intercepts PitchPrint's "Save for Later" action and redirects to account page with success message
 */

(function() {
  'use strict';

  // Use global debug flag from theme settings
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[Save for Later]', ...args);

  log('Interceptor initialized');

  // Wait for PitchPrint to be available
  function init() {
    const checkInterval = setInterval(() => {
      if (window.ppclient && window.app && window.app.events) {
        clearInterval(checkInterval);
        setupInterceptor();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }

  function setupInterceptor() {
    log('Setting up PitchPrint event interceptor');

    try {
      // Listen for PitchPrint's project-saved event
      window.app.events.on('project-saved', handleProjectSaved);

      // Also intercept the save-for-later action directly
      if (window.app && window.app.model && window.app.model.runtime) {
        const originalAct = window.app.model.runtime.act || {};

        // Create a proxy to detect when saveForLater flag is set
        window.app.model.runtime.act = new Proxy(originalAct, {
          set: function(target, property, value) {
            if (property === 'saveForLater' && value === true) {
              log('Detected saveForLater flag');
              target[property] = value;

              // Wait a moment for the save to complete, then redirect
              setTimeout(() => {
                redirectToAccount();
              }, 1500);

              return true;
            }
            target[property] = value;
            return true;
          }
        });
      }

      log('Interceptor setup complete');
    } catch (error) {
      console.error('[Save for Later] Error setting up interceptor:', error);
    }
  }

  function handleProjectSaved(event) {
    log('Project saved event:', event);

    // 2026-04-30: Auto-saves carry the saveForLater flag in their payload (PitchPrint's
    // app.js save service includes it on every save). Without this guard, every 5-minute
    // auto-save was triggering redirectToAccount() → page nav mid-iframe-render → crash.
    // FullStory captured the chain: auto-saved → ATC click → "data:{clear:true}" → Uncaught Script error.
    if (event?.data?.autoSaved || event?.autoSaved) {
      log('Skipping autoSaved event — save-for-later redirect is user-action only');
      return;
    }

    // Check if this is a "save for later" action
    const isSaveForLater = window.app?.model?.runtime?.act?.saveForLater ||
                           event?.data?.saveForLater;

    if (isSaveForLater) {
      log('Detected save-for-later action, redirecting...');

      // Small delay to ensure save completes
      setTimeout(() => {
        redirectToAccount();
      }, 1500);
    }
  }

  function redirectToAccount() {
    log('Redirecting to My Designs page with success message');

    // Close PitchPrint editor if open
    if (window.ppclient && typeof window.ppclient.closeApp === 'function') {
      try {
        window.ppclient.closeApp();
      } catch (e) {
        log('Could not close PitchPrint app:', e);
      }
    }

    // Redirect to My Designs page with saved flag
    // NOTE: We use /pages/my-designs instead of /account because Shopify's new customer
    // accounts system automatically redirects /account to my.popsigns.co
    window.location.href = '/pages/my-designs?saved=true';
  }

  // Alternative: Intercept button clicks directly
  function interceptSaveForLaterButton() {
    // Look for the save-for-later button in PitchPrint UI
    const observer = new MutationObserver((mutations) => {
      // Find buttons with data-cmd="save-for-later"
      const saveButtons = document.querySelectorAll('[data-cmd="save-for-later"]');

      // Also find buttons by text content (manual :contains equivalent)
      const allButtons = document.querySelectorAll('button');
      allButtons.forEach(button => {
        // The custom wishlist's cart "Save for later" control shares this label
        // but must NOT redirect to My Designs — it moves the line to the wishlist
        // and stays on the page. Skip it (and anything inside the wishlist UI).
        if (button.classList.contains('wishlist-save-later') || button.closest('.wishlist-drawer, [data-wishlist-page]')) return;
        const text = button.textContent || button.innerText;
        if (text && text.toLowerCase().includes('save for later')) {
          // Add to our collection by processing it
          if (button.dataset.intercepted) return;

          button.dataset.intercepted = 'true';
          button.addEventListener('click', (e) => {
            log('Save button clicked');

            // Let PitchPrint handle the save first
            setTimeout(() => {
              redirectToAccount();
            }, 2000);
          });
        }
      });

      // Process data-cmd buttons
      saveButtons.forEach(button => {
        if (button.dataset.intercepted) return;

        button.dataset.intercepted = 'true';
        button.addEventListener('click', (e) => {
          log('Save button clicked');

          // Let PitchPrint handle the save first
          setTimeout(() => {
            redirectToAccount();
          }, 2000);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // **CRITICAL**: Override PitchPrint's redirect URL configuration
  function overridePitchPrintRedirect() {
    log('Setting up PitchPrint redirect override');

    // Method 1: Override the client configuration redirect URL
    const checkPPClient = setInterval(() => {
      if (window.ppclient && window.ppclient._options) {
        clearInterval(checkPPClient);

        log('Found ppclient, checking redirect URL...');

        // Check current redirect URL
        const currentRedirect = window.ppclient._options.userId;
        log('Current redirect config:', window.ppclient._options);

        // Try to override the account URL in PitchPrint config
        if (window.ppclient._accountUrl) {
          log('Original account URL:', window.ppclient._accountUrl);
          window.ppclient._accountUrl = '/account?saved=true#designs';
          log('Overridden account URL to:', window.ppclient._accountUrl);
        }
      }
    }, 100);

    setTimeout(() => clearInterval(checkPPClient), 5000);

    // Method 2: Use MutationObserver to watch for navigation attempts
    let redirectAttempted = false;
    const originalURL = window.location.href;

    // Watch for URL changes via history API
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(state, title, url) {
      log('history.pushState called:', url);
      if (url && url.includes('my.popsigns.co')) {
        log('Blocking history.pushState to my.popsigns.co');
        return;
      }
      return originalPushState.apply(this, arguments);
    };

    history.replaceState = function(state, title, url) {
      log('history.replaceState called:', url);
      if (url && url.includes('my.popsigns.co')) {
        log('Blocking history.replaceState to my.popsigns.co');
        return;
      }
      return originalReplaceState.apply(this, arguments);
    };

    // Method 3: Monitor navigation and prevent it
    window.addEventListener('beforeunload', function(e) {
      const isSaveForLater = window.app?.model?.runtime?.act?.saveForLater;

      if (isSaveForLater && !redirectAttempted) {
        log('beforeunload detected with saveForLater flag');
        redirectAttempted = true;

        // Cancel the navigation
        e.preventDefault();
        e.returnValue = '';

        // Redirect to our account page instead
        setTimeout(() => {
          log('Redirecting to account page');
          window.location.href = '/account?saved=true#designs';
        }, 100);

        return '';
      }
    });

    log('Redirect override mechanisms installed');
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      overridePitchPrintRedirect();
    });
  } else {
    init();
    overridePitchPrintRedirect();
  }

  // Also try button interception as fallback
  interceptSaveForLaterButton();

})();
